import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import ContentLoader from "react-content-loader";
import { CartContext } from "../../../contexts/CartContext";
import { langComponent } from "../../../constants";

import { PAGE_STATE } from "../../../constants";
import useWorks from "../../../hooks/useWorks";
import useUsers from "../../../hooks/useUsers";
import useJournals from "../../../hooks/useJournals";
import AppContext from "../../../AppContext";

//component
import Card from "../Card";

//image
import { ReactComponent as ArrowRightSVG } from "../../../assets/workExchange/arrow-right-circle.svg";
import { ReactComponent as ArrowLeftSVG } from "../../../assets/workExchange/arrow-left-circle.svg";
import { ReactComponent as ArrowRightDisabledSVG } from "../../../assets/workExchange/arrow-right-circle-disabled.svg";
import { ReactComponent as ArrowLeftDisabledSVG } from "../../../assets/workExchange/arrow-left-circle-disabled.svg";
import { ReactComponent as ArrowRightThinBlackSvg } from "../../../assets/icons/arrow-right-thin.svg";
import { ReactComponent as EmptySvg } from "../../../assets/icons/empty-projects.svg";

// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";

const Section = ({ type }) => {
  const { designStyles, user, getLang } = useContext(AppContext);
  // const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [projects, setProjects] = useState(undefined);
  const swiperRef = useRef(null);
  const { queryWorks } = useWorks();
  const { queryUsers, queryCompanies } = useUsers();
  const [workType, setWorkType] = useState(undefined);
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderPerView, setSliderPerView] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const { queryJournals } = useJournals();
  const [journals, setJournals] = useState([]);
  const [currentBreakpoint, setCurrentBreakpoint] = useState(0);
  const [enabledPagination, setEnabledPagination] = useState(false);

  const { isFetchDetail, setIsFetchDetail, items } = useContext(CartContext);
  const TranslationCommon = getLang(langComponent.common);

  const _init = async () => {
    setIsFetchDetail(false);
    const filter = {
      "designType.workExchange": type,
      deleted: false,
      active: true,
    };

    const { data } = await queryWorks(
      type ? filter : null,
      1,
      4,
      {
        name: 1,
        createdBy: 1,
        desc: 1,
        brief: 1,
        price: 1,
        banner: 1,
        program: 1,
        renderer: 1,
        views: 1,
        designType: 1,
        discount: 1,
        createdOn: 1,
        company: 1,
      },
      {
        createdOn: -1,
      }
    );

    // * api query users
    const { data: _users } = await queryUsers(
      { _id: { $in: data.map((d) => d.createdBy) } },
      1,
      data.length,
      { firstname: 1, surname: 1, avatar: 1 }
    );

    const { data: companies } = await queryCompanies(
      {
        _id: {
          $in: data.map((d) => d.company ?? d.createdBy),
        },
      },
      null,
      -1,
      {
        _id: 1,
        name: 1,
        logo: 1,
      }
    );

    if (!data.length) setProjects([]);

    setProjects(
      data.map((w) => {
        const userFound = _users.find((u) => u._id === w.createdBy);
        const _company = companies.find(
          (company) => w?.company === company._id
        );

        return {
          ...w,
          user: _company
            ? {
                name: _company.name,
                avatar: _company.logo,
              }
            : {
                name: `${userFound.firstname} ${userFound.surname}`,
                avatar: userFound.avatar,
              },
        };
      })
    );

    const _workIdList = data?.map((item) => item._id);

    if (user?._id) {
      const { data: _journals } = await queryJournals({
        "details.work": {
          $in: _workIdList,
        },
        company: user?.company, // * Company
        createdBy: user?._id, // * User ที่ login
        active: true,
        deleted: false,
        status: 2,
      });
      setJournals(_journals);
    }
    setIsFetchDetail(true);
    setLoading(true);
    setPageState(PAGE_STATE.IDLE);
  };

  useEffect(() => {
    if (!designStyles) {
      return;
    }

    _init();
    setWorkType(designStyles.workExchanges.find((we) => we.value === type));
    setLoading(true);
    setPageState(PAGE_STATE.IDLE);
  }, [designStyles, user?._id, items]);

  const handleSeeMoreClick = () => {
    navigate(workType.value);
  };

  const ref = useCallback((node) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver((entries) => {
        const _width = entries[0].contentRect.width;

        if (_width < 668) {
          setEnabledPagination(true);
          return;
        } else {
          setEnabledPagination(false);
        }
      });
      resizeObserver.observe(node);
    }
  }, []);

  // if (pageState === PAGE_STATE.LOADING || !projects) {
  //   return <></>;
  // }
  if (!projects) return;
  const isDisablePrev = currentSlide === 0;
  const isDisableNext =
    currentBreakpoint == "320"
      ? projects.length == 1
        ? true
        : currentSlide + sliderPerView === projects.length
      : currentBreakpoint == "768"
      ? projects.length <= 2
        ? true
        : currentSlide + sliderPerView === projects.length
      : currentBreakpoint == "1200"
      ? projects.length <= 3
        ? true
        : currentSlide + sliderPerView === projects.length
      : false;

  return (
    <div className="work-exchange-main-section" ref={ref}>
      <div className="work-exchange-main-section-header">
        <span className="work-exchange-main-section-header__title">
          {workType.name}
        </span>
        <button
          className="work-exchange-main-section-header__see-more"
          onClick={handleSeeMoreClick}
        >
          <ArrowRightThinBlackSvg className="work-exchange-main-section-header__see-more__icon" />
          <span className="work-exchange-main-section-header__see-more__text">
            {TranslationCommon("See more")}
          </span>
        </button>
        <div className="work-exchange-main-section-header-arrows">
          <button
            className={[
              "work-exchange-main-section-header-arrows__button",
              "work-exchange-main-section-header-arrows__button--left",
              isDisablePrev
                ? "work-exchange-main-section-header-arrows__button--disabled"
                : "",
            ].join(" ")}
            onClick={() => {
              swiperRef.current.swiper.slidePrev();
            }}
          >
            {isDisablePrev ? <ArrowLeftDisabledSVG /> : <ArrowLeftSVG />}
          </button>
          <button
            className={[
              "work-exchange-main-section-header-arrows__button",
              isDisableNext
                ? "work-exchange-main-section-header-arrows__button--disabled"
                : "",
            ].join(" ")}
            onClick={() => {
              swiperRef.current.swiper.slideNext();
            }}
          >
            {isDisableNext ? <ArrowRightDisabledSVG /> : <ArrowRightSVG />}
          </button>
        </div>
      </div>
      <div className="work-exchange-main-section-gallery">
        {loading ? (
          <Swiper
            grabCursor={true}
            initialSlide={0}
            centeredSlides={false}
            spaceBetween={20}
            navigation={false}
            ref={swiperRef}
            modules={[Pagination, Navigation]}
            pagination={{
              clickable: true,
              enabled: enabledPagination,
            }}
            breakpoints={{
              320: {
                slidesPerView: 1,
              },
              768: {
                slidesPerView: 2,
              },
              1200: {
                slidesPerView: 3,
              },
            }}
            onSlideChange={(swiperCore) => {
              const { activeIndex, passedParams, currentBreakpoint } =
                swiperCore;
              setCurrentSlide(activeIndex);
              setSliderPerView(
                passedParams.breakpoints[currentBreakpoint].slidesPerView
              );
            }}
            onSwiper={(swiper) => {
              setCurrentBreakpoint(swiper.currentBreakpoint);
              setSliderPerView(swiper.activeIndex);
            }}
          >
            {projects.map((project, index) => (
              <SwiperSlide key={index}>
                <Card
                  {...project}
                  creator={project.user}
                  workJournals={journals}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="work-exchange-main-section-gallery-loader">
            {projects.length > 0 &&
              projects.map((project, index) => {
                if (index < 3) {
                  return (
                    <ContentLoader viewBox="0 0 620 782">
                      <rect
                        x="0"
                        y="0"
                        rx={20}
                        ry={20}
                        width="620"
                        height="100%"
                      />
                    </ContentLoader>
                  );
                }
              })}
          </div>
        )}
      </div>
      {projects.length == 0 && (
        <div className="work-exchange-main-notfound">
          <EmptySvg height={224} style={{ opacity: 0.3 }} />
          <span
            style={{
              fontSize: 16,
              color: "#9d9d9d",
              marginTop: -8,
            }}
          >
            No results found
          </span>
        </div>
      )}
    </div>
  );
};

Section.propTypes = {};

export default Section;
